import React from 'react';
import './DesktopNavigation.scss';
import Illustration from 'components/Illustrations/Illustration';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonPage,
  IonRow,
  IonSpinner
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'core/store';
import { useSelector } from 'react-redux';
import { DIGITAL_PAGES, FULL_SERVICE_PAGES, HKPlatform } from 'core/constants';
import { hasWarrantySubscription, openHelp } from 'core/util';
import { useCustomTokenLink } from 'core/hooks/useCustomTokenLink';
import HealthBanner from 'components/health-banner/HealthBanner';

type DesktopNavigationProps = {
  headerSlot?: any;
};

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  headerSlot,
  children,
  ...rest
}) => {
  const {
    currentHome,
    isExpired,
    builderLogo,
    warrantyURL,
    globalAccountSettings
  } = useSelector((state: RootState) => state.home);
  const { accessToken } = useSelector((state: RootState) => state.login);
  const { isDigital } = useSelector((state: RootState) => state.user);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );

  const location = useLocation();
  const history = useHistory();
  const { createCustomTokenLink } = useCustomTokenLink();

  const navigateTo = (route: string) => {
    const isExternal = /^https?:\/\//.test(route);
    if (isExternal && accessToken) {
      createCustomTokenLink(
        accessToken,
        globalAccountSettings?.avid_warranty_url!
      );
    } else {
      history.push(route);
    }
  };

  const isTutorialPage = () => {
    return location.pathname.match('/tutorial');
  };

  const isOnboardingPage = () => {
    return (
      location.pathname.match('/onboarding/what-type-of-home-do-you-own') ||
      location.pathname.match('/onboarding/what-is-your-home-address') ||
      location.pathname.match(
        '/onboarding/what-are-you-looking-to-get-out-of-this-app'
      ) ||
      location.pathname.match('/onboarding/what-type-of-homeowner-are-you') ||
      location.pathname.match('/onboarding/how-long-do-you-expect-to-stay')
    );
  };

  const NavBar = () => {
    let navigationLinks =
      isDigital && !isOnboardingPage() ? DIGITAL_PAGES : FULL_SERVICE_PAGES;

    if (
      warrantyURL &&
      currentHome &&
      hasWarrantySubscription(currentHome?.subscriptions)
    ) {
      navigationLinks = navigationLinks.map((page) => {
        if (page.title === 'Warranty') {
          return { ...page, path: warrantyURL };
        } else {
          return page;
        }
      });
    } else {
      navigationLinks = navigationLinks.filter(
        (page) => page.title !== 'Warranty'
      );
    }

    const isPageSelected = (pagePath: string) => {
      if (!pagePath) return false;
      const matchingPage = navigationLinks
        .filter((p) => p.path && location.pathname.includes(p.path))
        .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
      return matchingPage?.path === pagePath;
    };

    return (
      <>
        {isDigital &&
          !isOnboardingPage() &&
          navigationLinks.map((page, i) => {
            return (
              <div key={`menu-item-${i}`}>
                {isExpired && page.path === '/account' && (
                  <IonItem
                    className={isPageSelected(page.path) ? 'selected' : ''}
                    onClick={() => navigateTo(page.path)}
                  >
                    <IonIcon icon={page.icon} slot="start" />
                    {page.title}
                  </IonItem>
                )}
                {!isExpired && (
                  <IonItem
                    className={isPageSelected(page.path) ? 'selected' : ''}
                    onClick={() => navigateTo(page.path)}
                  >
                    <IonIcon icon={page.icon} slot="start" />
                    {page.title}
                  </IonItem>
                )}
              </div>
            );
          })}
        {!isDigital &&
          navigationLinks.map((page, i) => {
            return (
              <IonItem
                key={`menu-item-${i}`}
                className={isPageSelected(page.path) ? 'selected' : ''}
                onClick={() => navigateTo(page.path)}
              >
                <IonIcon icon={page.icon} slot="start" />
                {page.title}
              </IonItem>
            );
          })}
      </>
    );
  };

  return platformType === HKPlatform.DESKTOP &&
    isDesktopWidth &&
    !isTutorialPage() &&
    accessToken ? (
    <IonPage className="hk-desktop-nav">
      <div className="hk-desktop-nav-header-container" {...rest}>
        <div
          className={`hk-desktop-nav-header ${
            isDigital && 'hk-desktop-digital-nav-header'
          }`}
        >
          <div className="hk-desktop-nav-header-top">
            <div onClick={() => navigateTo('/dashboard')}>
              {builderLogo ? (
                <Illustration
                  image={builderLogo}
                  className="hk-desktop-nav-header-top-logo"
                />
              ) : (
                <IonSpinner name="crescent" />
              )}
            </div>
            <IonRow className="hk-desktop-nav-header-top-nav">
              <NavBar />
              <IonButton
                className="hk-desktop-nav-header-top-help"
                color="secondary"
                onClick={() => openHelp(platformType)}
              >
                Help
              </IonButton>
            </IonRow>
          </div>
        </div>
        <HealthBanner />
      </div>
      <div className="hk-desktop-nav-body">{children}</div>
    </IonPage>
  ) : (
    <>{children}</>
  );
};

export default DesktopNavigation;
